/**
 * The last S-Corp report for each project, kept in this browser.
 *
 * The server holds a run only while it is in use (two idle hours); the report
 * itself — the register, timeline, exceptions, answers and requests, plus the
 * Word document — is kept here so it can be reopened later. Keyed by project,
 * one report per project, and in an IndexedDB database of its own so that
 * signing out deletes it (IDB_DATABASES in src/utils/clearEngagementState.ts)
 * and deleting the project removes its entry (src/components/Home.tsx).
 *
 * Every call swallows storage failures: a private window or a full disk must
 * not stop the report from being shown; it is simply not kept.
 */
import type { ReportData } from "./components/ScorpReport";

export const SCORP_DB = "tax-scorp";
const STORE = "reports";

export interface SavedScorpReport {
  projectId: string;
  /** The run it came from; its page images are available while the server still holds it. */
  jobId: string;
  savedAt: string;
  data: ReportData;
  /** The Word document, fetched when the run finished. */
  docx: Blob | null;
}

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(SCORP_DB, 1);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE, { keyPath: "projectId" });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function withStore<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await open();
  try {
    return await new Promise<T>((resolve, reject) => {
      const req = fn(db.transaction(STORE, mode).objectStore(STORE));
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  } finally {
    db.close();
  }
}

export async function loadScorpReport(projectId: string): Promise<SavedScorpReport | null> {
  try {
    return ((await withStore("readonly", (s) => s.get(projectId))) as SavedScorpReport | undefined) ?? null;
  } catch {
    return null;
  }
}

export async function saveScorpReport(report: SavedScorpReport): Promise<void> {
  try {
    await withStore("readwrite", (s) => s.put(report));
  } catch {
    /* not kept; the report on screen is unaffected */
  }
}

export async function forgetScorpReport(projectId: string): Promise<void> {
  try {
    await withStore("readwrite", (s) => s.delete(projectId));
  } catch {
    /* nothing to remove, or storage unavailable */
  }
}
